import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  ListItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

type Props<T extends string> = {
  title: string;
  items: T[];
  itemLabel: (item: T) => string;
  selected: T[];
  onChange: (next: T[]) => void;
  withDivider?: boolean;
};

export function CheckboxGroupSection<T extends string>({
  title,
  items,
  itemLabel,
  selected,
  onChange,
  withDivider = true,
}: Props<T>) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();

  const allSelected = items.length > 0 && items.every((it) => selected.includes(it));

  const toggleAll = () => {
    onChange(allSelected ? [] : [...items]);
  };

  const toggleItem = (item: T, checked: boolean) => {
    onChange(checked ? [...selected, item] : selected.filter((s) => s !== item));
  };

  return (
    <>
      <ListItem sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          {items.length > 1 && (
            <Button
              size="small"
              onClick={toggleAll}
              sx={{ textTransform: 'none', py: 0, minWidth: 0, color: 'text.secondary' }}>
              <Typography variant="caption">
                {allSelected ? t('calendar.filter.deselect_all') : t('calendar.filter.select_all')}
              </Typography>
            </Button>
          )}
        </Box>
      </ListItem>
      <ListItem>
        <Stack width="100%" gap={0.5}>
          {items.map((item) => (
            <FormControlLabel
              key={item}
              label={itemLabel(item)}
              control={
                <Checkbox
                  size={isDesktop ? 'small' : 'medium'}
                  checked={selected.includes(item)}
                  onChange={(e) => toggleItem(item, e.target.checked)}
                />
              }
            />
          ))}
        </Stack>
      </ListItem>
      {withDivider && <Divider />}
    </>
  );
}
